import { z } from 'zod';
import { type Role } from './schemas/common.js';

/**
 * Rôles, actions et interrupteurs. Source unique de vérité : l'API vérifie avec
 * `requirePermission(action)`, l'interface ne fait qu'en déduire ce qu'elle affiche.
 */

export const ACTIONS = [
  'intervention.view',
  'intervention.create',
  'intervention.update',
  'intervention.ready',
  'intervention.release',
  'work.record',
  'media.upload',
  'media.publish',
  'complement.draft',
  'complement.send',
  'complement.record_decision',
  'question.answer',
  'portal.manage',
  'customer.manage',
  'amounts.view',
  'storage.waive',
  'team.manage',
  'settings.manage',
  'audit.view',
  // Vague 5 : accepter, refuser ou retirer l'accès du support au contenu du garage (propriétaire seul).
  'support.manage',
  // Correctifs vague 6 (décision L0) : effacer définitivement les données d'un client (propriétaire seul,
  // sans interrupteur ; jamais le support).
  'customer.erase',
] as const;

export const Action = z.enum(ACTIONS);
export type Action = z.infer<typeof Action>;

export const TOGGLES = ['canViewAmounts', 'canSendWithoutValidation', 'canManageCustomers'] as const;
export type Toggle = (typeof TOGGLES)[number];

export const Toggles = z.strictObject({
  canViewAmounts: z.boolean(),
  canSendWithoutValidation: z.boolean(),
  canManageCustomers: z.boolean(),
});
export type Toggles = z.infer<typeof Toggles>;

/** Actions accordées par chaque interrupteur, quand le rôle autorise cet interrupteur. */
export const TOGGLE_ACTIONS: Record<Toggle, readonly Action[]> = {
  canViewAmounts: ['amounts.view'],
  canSendWithoutValidation: ['complement.send', 'media.publish', 'complement.record_decision'],
  canManageCustomers: ['customer.manage'],
};

interface RoleDefinition {
  /** Actions toujours accordées. */
  base: readonly Action[];
  /** Interrupteurs que le propriétaire peut régler pour ce rôle. */
  allowedToggles: readonly Toggle[];
  /** Valeurs par défaut à l'invitation. */
  defaultToggles: Toggles;
}

const ALL: readonly Action[] = ACTIONS;
const ON: Toggles = { canViewAmounts: true, canSendWithoutValidation: true, canManageCustomers: true };
const OFF: Toggles = { canViewAmounts: false, canSendWithoutValidation: false, canManageCustomers: false };

export const ROLE_DEFINITIONS: Record<Role, RoleDefinition> = {
  owner: { base: ALL, allowedToggles: [], defaultToggles: ON },
  partner: {
    base: ALL.filter((a) => a !== 'settings.manage' && a !== 'support.manage' && a !== 'customer.erase'),
    allowedToggles: [],
    defaultToggles: ON,
  },
  frontdesk: {
    base: [
      'intervention.view',
      'intervention.create',
      'intervention.update',
      'intervention.ready',
      'intervention.release',
      'work.record',
      'media.upload',
      'complement.draft',
      'question.answer',
      'portal.manage',
      'audit.view',
    ],
    allowedToggles: TOGGLES,
    defaultToggles: ON,
  },
  mechanic: {
    base: [
      'intervention.view',
      'intervention.update',
      'intervention.ready',
      'work.record',
      'media.upload',
      'complement.draft',
      'audit.view',
    ],
    allowedToggles: TOGGLES,
    defaultToggles: OFF,
  },
  readonly: {
    base: ['intervention.view', 'audit.view'],
    allowedToggles: ['canViewAmounts'],
    defaultToggles: OFF,
  },
};

/** Rôles qu'un membre ayant `team.manage` peut attribuer. Seul un propriétaire nomme un propriétaire. */
export function assignableRoles(actorRole: Role): Role[] {
  return actorRole === 'owner'
    ? ['owner', 'partner', 'frontdesk', 'mechanic', 'readonly']
    : ['partner', 'frontdesk', 'mechanic', 'readonly'];
}

/** Interrupteurs effectifs : ceux que le rôle n'autorise pas sont ramenés à leur valeur de rôle. */
export function normalizeToggles(role: Role, toggles: Partial<Toggles> | undefined): Toggles {
  const def = ROLE_DEFINITIONS[role];
  const result = { ...def.defaultToggles };
  for (const t of def.allowedToggles) {
    const v = toggles?.[t];
    if (typeof v === 'boolean') result[t] = v;
  }
  return result;
}

export function effectivePermissions(role: Role, toggles: Partial<Toggles> | undefined): Set<Action> {
  const def = ROLE_DEFINITIONS[role];
  const set = new Set<Action>(def.base);
  const t = normalizeToggles(role, toggles);
  for (const toggle of def.allowedToggles) {
    if (t[toggle]) for (const a of TOGGLE_ACTIONS[toggle]) set.add(a);
  }
  // Le lien portail donne accès à tout ce que voit le client (montants, décision) : seul un membre
  // qui voit les montants ET peut saisir une décision peut le générer ou le révoquer (revue L8, B1/B2).
  if (!(set.has('amounts.view') && set.has('complement.record_decision'))) set.delete('portal.manage');
  return set;
}

export function can(role: Role, toggles: Partial<Toggles> | undefined, action: Action): boolean {
  return effectivePermissions(role, toggles).has(action);
}

/** Rôle plateforme : crée les garages, aucun accès au contenu des dossiers en V0. */
export const PLATFORM_ROLE = 'platform_admin' as const;

// ─── Libellés et aide des rôles (vague 4) ───────────────────────────────────

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Propriétaire',
  partner: 'Associé',
  frontdesk: 'Accueil',
  mechanic: 'Mécanicien',
  readonly: 'Lecture seule',
};

export const TOGGLE_LABELS: Record<Toggle, string> = {
  canViewAmounts: 'Voir les montants',
  canSendWithoutValidation: 'Envoyer au client sans validation',
  canManageCustomers: 'Gérer la fiche des clients',
};

export const ACTION_LABELS: Record<Action, string> = {
  'intervention.view': 'Consulter les dossiers',
  'intervention.create': 'Enregistrer l’entrée d’un véhicule',
  'intervention.update': 'Modifier un dossier (statut, emplacement, demande, date d’entrée)',
  'intervention.ready': 'Déclarer le véhicule prêt',
  'intervention.release': 'Enregistrer la sortie du véhicule',
  'work.record': 'Saisir les travaux réalisés',
  'media.upload': 'Prendre et classer des photos',
  'media.publish': 'Publier des photos au client',
  'complement.draft': 'Préparer des travaux complémentaires',
  'complement.send': 'Envoyer des travaux complémentaires au client',
  'complement.record_decision': 'Saisir la décision du client (téléphone, sur place)',
  'question.answer': 'Répondre aux questions des clients',
  'portal.manage': 'Partager ou révoquer le lien de suivi du client',
  'customer.manage': 'Créer et modifier les fiches clients',
  'amounts.view': 'Voir les montants',
  'storage.waive': 'Renoncer aux frais de gardiennage',
  'team.manage': 'Gérer l’équipe (invitations, rôles, interrupteurs)',
  'settings.manage': 'Modifier les réglages et la marque du garage',
  'audit.view': 'Consulter le journal d’activité',
  'support.manage': 'Accepter, refuser ou retirer l’accès du support',
  'customer.erase': 'Effacer définitivement les données d’un client',
};

/**
 * Une ligne d'aide et les actions qu'elle décrit. `requires` : interrupteurs à activer pour que la
 * ligne soit vraie. Un test vérifie chaque ligne contre la matrice (`effectivePermissions`).
 * `actions` vide : règle d'organisation sans action dédiée (ex. dernier propriétaire).
 */
export interface RoleHelpItem {
  text: string;
  actions: readonly Action[];
  requires?: readonly Toggle[];
}

export interface RoleHelpDetail {
  summary: string;
  can: readonly RoleHelpItem[];
  cannot: readonly RoleHelpItem[];
}

const TOGGLE_HELP_ITEMS: Record<Toggle, RoleHelpItem> = {
  canViewAmounts: {
    text: 'Avec « Voir les montants » : voir les prix, totaux et frais de gardiennage',
    actions: ['amounts.view'],
    requires: ['canViewAmounts'],
  },
  canSendWithoutValidation: {
    text: 'Avec « Envoyer au client sans validation » : publier les photos, envoyer les travaux complémentaires et saisir la décision du client',
    actions: ['media.publish', 'complement.send', 'complement.record_decision'],
    requires: ['canSendWithoutValidation'],
  },
  canManageCustomers: {
    text: 'Avec « Gérer la fiche des clients » : créer et modifier les fiches clients',
    actions: ['customer.manage'],
    requires: ['canManageCustomers'],
  },
};

const RECEIVE_TO_RELEASE: RoleHelpItem = {
  text: 'Recevoir les véhicules, suivre les dossiers, déclarer prêt et enregistrer la sortie',
  actions: ['intervention.view', 'intervention.create', 'intervention.update', 'intervention.ready', 'intervention.release'],
};
const AUDIT_ITEM: RoleHelpItem = { text: 'Consulter le journal d’activité', actions: ['audit.view'] };

const MANAGEMENT_CAN: readonly RoleHelpItem[] = [
  RECEIVE_TO_RELEASE,
  { text: 'Saisir les travaux, prendre et publier les photos', actions: ['work.record', 'media.upload', 'media.publish'] },
  {
    text: 'Préparer et envoyer les travaux complémentaires, saisir la décision du client',
    actions: ['complement.draft', 'complement.send', 'complement.record_decision'],
  },
  { text: 'Répondre aux clients et partager le lien de suivi', actions: ['question.answer', 'portal.manage'] },
  { text: 'Gérer les fiches clients et voir tous les montants', actions: ['customer.manage', 'amounts.view'] },
  { text: 'Renoncer aux frais de gardiennage', actions: ['storage.waive'] },
  AUDIT_ITEM,
];

export const ROLE_HELP_DETAILS: Record<Role, RoleHelpDetail> = {
  owner: {
    summary: 'Dirige le garage : tous les droits, y compris les réglages, la marque et la nomination d’autres propriétaires.',
    can: [
      ...MANAGEMENT_CAN,
      { text: 'Gérer l’équipe, y compris nommer ou modifier un propriétaire', actions: ['team.manage'] },
      { text: 'Modifier les réglages, la marque et les conditions de gardiennage', actions: ['settings.manage'] },
      { text: 'Accepter, refuser ou retirer l’accès du support au garage', actions: ['support.manage'] },
      { text: 'Effacer définitivement les données d’un client (RGPD)', actions: ['customer.erase'] },
    ],
    cannot: [{ text: 'Rétrograder ou retirer le dernier propriétaire du garage', actions: [] }],
  },
  partner: {
    summary: 'Codirige le garage : tous les droits sur les dossiers et l’équipe, sauf les réglages du garage.',
    can: [...MANAGEMENT_CAN, { text: 'Gérer l’équipe (hors propriétaires)', actions: ['team.manage'] }],
    cannot: [
      { text: 'Modifier les réglages, la marque et les conditions de gardiennage', actions: ['settings.manage'] },
      { text: 'Accepter ou retirer l’accès du support', actions: ['support.manage'] },
      { text: 'Effacer définitivement les données d’un client (réservé au propriétaire)', actions: ['customer.erase'] },
      { text: 'Nommer ou modifier un propriétaire', actions: [] },
    ],
  },
  frontdesk: {
    summary: 'Accueille les clients et tient les dossiers : entrée, échanges avec le client, sortie.',
    can: [
      RECEIVE_TO_RELEASE,
      { text: 'Saisir les travaux, prendre et classer les photos', actions: ['work.record', 'media.upload'] },
      { text: 'Préparer des travaux complémentaires', actions: ['complement.draft'] },
      { text: 'Répondre aux questions des clients', actions: ['question.answer'] },
      AUDIT_ITEM,
      TOGGLE_HELP_ITEMS.canViewAmounts,
      TOGGLE_HELP_ITEMS.canSendWithoutValidation,
      TOGGLE_HELP_ITEMS.canManageCustomers,
      {
        text: 'Avec « Voir les montants » et « Envoyer au client sans validation » : partager ou révoquer le lien de suivi',
        actions: ['portal.manage'],
        requires: ['canViewAmounts', 'canSendWithoutValidation'],
      },
    ],
    cannot: [
      { text: 'Renoncer aux frais de gardiennage', actions: ['storage.waive'] },
      { text: 'Gérer l’équipe', actions: ['team.manage'] },
      { text: 'Modifier les réglages du garage', actions: ['settings.manage'] },
      { text: 'Accepter ou retirer l’accès du support', actions: ['support.manage'] },
      { text: 'Effacer définitivement les données d’un client', actions: ['customer.erase'] },
    ],
  },
  mechanic: {
    summary: 'Travaille sur les véhicules : travaux, photos et travaux complémentaires, envoyés au client après validation.',
    can: [
      {
        text: 'Consulter et mettre à jour les dossiers, déclarer le véhicule prêt',
        actions: ['intervention.view', 'intervention.update', 'intervention.ready'],
      },
      { text: 'Saisir les travaux, prendre et classer les photos', actions: ['work.record', 'media.upload'] },
      { text: 'Préparer des travaux complémentaires, à faire valider', actions: ['complement.draft'] },
      AUDIT_ITEM,
      TOGGLE_HELP_ITEMS.canViewAmounts,
      TOGGLE_HELP_ITEMS.canSendWithoutValidation,
      TOGGLE_HELP_ITEMS.canManageCustomers,
    ],
    cannot: [
      { text: 'Enregistrer l’entrée ou la sortie d’un véhicule', actions: ['intervention.create', 'intervention.release'] },
      { text: 'Répondre aux clients ou partager le lien de suivi', actions: ['question.answer', 'portal.manage'] },
      { text: 'Renoncer aux frais de gardiennage', actions: ['storage.waive'] },
      { text: 'Gérer l’équipe ou les réglages du garage', actions: ['team.manage', 'settings.manage'] },
      { text: 'Accepter ou retirer l’accès du support', actions: ['support.manage'] },
      { text: 'Effacer définitivement les données d’un client', actions: ['customer.erase'] },
    ],
  },
  readonly: {
    summary: 'Consulte les dossiers sans rien modifier (expert, comptable, apprenti).',
    can: [
      { text: 'Consulter les dossiers et le journal d’activité', actions: ['intervention.view', 'audit.view'] },
      TOGGLE_HELP_ITEMS.canViewAmounts,
    ],
    cannot: [
      {
        text: 'Modifier un dossier, saisir des travaux ou des photos, préparer des travaux complémentaires',
        actions: [
          'intervention.create',
          'intervention.update',
          'intervention.ready',
          'intervention.release',
          'work.record',
          'media.upload',
          'complement.draft',
        ],
      },
      {
        text: 'Écrire au client, lui envoyer quoi que ce soit ou saisir sa décision',
        actions: ['question.answer', 'media.publish', 'complement.send', 'complement.record_decision', 'portal.manage'],
      },
      {
        text: 'Gérer les clients, l’équipe, les réglages ou les frais de gardiennage',
        actions: ['customer.manage', 'customer.erase', 'team.manage', 'settings.manage', 'storage.waive', 'support.manage'],
      },
    ],
  },
};

export interface RoleHelp {
  summary: string;
  can: string[];
  cannot: string[];
}

/** Aide affichée à l'invitation et sur la fiche d'un membre : ce que le rôle permet, et ce qu'il ne permet pas. */
export const ROLE_HELP = Object.fromEntries(
  Object.entries(ROLE_HELP_DETAILS).map(([role, d]) => [
    role,
    { summary: d.summary, can: d.can.map((i) => i.text), cannot: d.cannot.map((i) => i.text) },
  ]),
) as Record<Role, RoleHelp>;

// ─── Accès support (vague 5) ────────────────────────────────────────────────

/** Portée d'un accès support accordé par le propriétaire : lecture seule par défaut. */
export const SUPPORT_SCOPES = ['read_only', 'full'] as const;
type SupportScopeName = (typeof SUPPORT_SCOPES)[number];

/**
 * Actions jamais accordées au support, quelle que soit la portée : équipe, réglages, accès support,
 * `portal.manage` (un lien de suivi contient un jeton : le support ne voit jamais de jeton portail),
 * effacement d'un client, et (audit S36) tout ce qui engage le client ou renonce à une somme due :
 * une décision au nom du client ne vient que du client ou d'un membre du garage.
 */
export const SUPPORT_EXCLUDED_ACTIONS: readonly Action[] = [
  'team.manage',
  'settings.manage',
  'support.manage',
  'portal.manage',
  'customer.erase',
  'complement.record_decision',
  'storage.waive',
];

/**
 * Droits effectifs d'une session support, recalculés à chaque requête depuis l'accès accordé :
 * - `read_only` : consultation (`intervention.view`, `audit.view`) ; toute méthode autre que GET est
 *   en plus refusée par le cœur ;
 * - `full` : actions d'un associé, sans `SUPPORT_EXCLUDED_ACTIONS`.
 * Dans les deux cas, `amounts.view` seulement si le propriétaire a rendu les montants visibles.
 */
export function supportPermissions(scope: SupportScopeName, amountsVisible: boolean): Set<Action> {
  const base: readonly Action[] =
    scope === 'full'
      ? ROLE_DEFINITIONS.partner.base.filter((a) => !SUPPORT_EXCLUDED_ACTIONS.includes(a))
      : ['intervention.view', 'audit.view'];
  const set = new Set<Action>(base);
  if (amountsVisible) set.add('amounts.view');
  else set.delete('amounts.view');
  return set;
}
